import { inject, Injectable } from '@angular/core';

import { Money, MoneyOperationService } from '@ts/shared/money/util-core';

import { OrderLine } from './order.model';
import { OrderReturnLine } from './order-return.model';

/**
 * Domain money math for order lines. Returns are not priced per line by the
 * backend, so the value kept/returned is derived client-side from
 * `quantity_return_cached` and `unit_price`. Mirrors `OrderSummaryLineService`.
 */
@Injectable({
  providedIn: 'root',
})
export class OrderLineService {
  private moneyOperationService = inject(MoneyOperationService);

  /** Quantity kept by the customer after returns (purchased - returned). */
  getNetQuantity(orderLine: OrderLine): number {
    return orderLine.quantity - orderLine.quantity_return_cached;
  }

  /**
   * Cap for editing an existing return line's quantity. `quantity_return_cached`
   * already counts this line once the return is enabled, so the line's own
   * quantity is the floor — otherwise editing an enabled return could not even
   * restore what `OrderReturn.check_can_enable` already accepted.
   */
  getReturnableQuantity(orderReturnLine: OrderReturnLine): number {
    return Math.max(
      this.getNetQuantity(orderReturnLine.order_line),
      orderReturnLine.quantity,
    );
  }

  /**
   * Line value kept after returns (total - returned value), so net value +
   * returned value always reconciles to the line total.
   */
  getNetValue(orderLine: OrderLine): Money {
    return this.moneyOperationService.moneySubtract(
      orderLine.total_price,
      this.getReturnedValue(orderLine),
    );
  }

  /**
   * Returned units valued at unit price. Always positive — callers that render
   * money flowing back out negate it themselves.
   */
  getReturnedValue(orderLine: OrderLine): Money {
    return this.moneyOperationService.moneyMultiply(
      orderLine.unit_price,
      orderLine.quantity_return_cached,
    );
  }

  /** Returned value across lines, so a receipt can total its return block. */
  getReturnedValueTotal(orderLines: readonly OrderLine[]): Money {
    return orderLines.reduce(
      (total, orderLine) =>
        this.moneyOperationService.moneyAdd(
          total,
          this.getReturnedValue(orderLine),
        ),
      this.moneyOperationService.MONEY_ZERO,
    );
  }
}
